// src/services/authService.js
import { auth, db } from "firebase.js";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
} from "firebase/auth";
import {
  getDoc,
  setDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import * as Sentry from "@sentry/react";

const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Simple email format check
  return emailRegex.test(email);
};

// Register new user
export const registerUser = async (
  email,
  password,
  name,
  phone,
  role = "user"
) => {
  try {
    if (!isValidEmail(email)) {
      throw new Error("Invalid email format.");
    }

    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    await sendEmailVerification(user);

    // Save user info in Firestore
    await setDoc(doc(db, "users", user.uid), {
      uid: user.uid,
      name,
      email,
      createdAt: serverTimestamp(),
      phone,
      role, // Default: "user"
      verified: false,
    });

    return user;
  } catch (error) {
    throw new Error(error.message);
  }
};

export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    //  Ensure the latest email verification status is fetched
    await user.reload();

    if (!user.emailVerified) {
      throw new Error("Please verify your email before logging in.");
    }

    //  Fetch the user's profile from Firestore
    const userRef = doc(db, "users", user.uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();

      //  If Firestore says "verified: false", but user is actually verified, update Firestore
      if (!userData.verified) {
        await updateDoc(userRef, { verified: true });
      }

      // 🔁 Set createdAt from Auth if missing
      if (!userData.createdAt) {
        await updateDoc(userRef, {
          createdAt: new Date(user.metadata.creationTime),
        });
      }

      //  Return user data with name from Firestore
      return {
        uid: user.uid,
        email: user.email,
        name: userData.name || "Utilisateur", // Get name from Firestore
        role: userData.role || "user",
        master: userData.master || false,
        adminFor: userData.adminFor || "",
        photoURL: user.photoURL || null, // Keep photoURL if available
      };
    } else {
      throw new Error("User profile not found in Firestore.");
    }
  } catch (error) {
    throw new Error(error.message);
  }
};

// Logout user
export const logoutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    throw new Error(error.message);
  }
};

export const resendVerificationEmail = async (user) => {
  try {
    if (!user) {
      throw new Error("No user found.");
    }
    await sendEmailVerification(user);
    console.log("Verification email sent again.");
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error resending email:", error.message);
  }
};

//  Reset Password Function
export const resetPassword = async (email) => {
  if (!email) throw new Error("Veuillez entrer votre adresse e-mail.");

  try {
    await sendPasswordResetEmail(auth, email);
    return "Un lien de réinitialisation a été envoyé à votre adresse e-mail.";
  } catch (error) {
    throw new Error(error.message);
  }
};

export const resendActivationEmail = async (email) => {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("User not found. Please log in first.");
  }

  if (user.email !== email) {
    throw new Error("This email is not associated with your account.");
  }

  await sendEmailVerification(user);
  return "Activation email sent! Please check your inbox.";
};
